/**
 * The voices that the user can pick for the voiceover of an AI reel (spec §9.5). Every row is a
 * Gemini prebuilt voice. The `gender` tags come from the Gemini voice list. A person confirms them
 * when the voice samples are generated; fix a wrong tag in this table, not in code. The file has
 * no imports, so every project can use it.
 */

export const REEL_VOICE_IDS = ['kore', 'zephyr', 'aoede', 'sulafat', 'puck', 'charon', 'achird', 'algenib'] as const;

export type ReelVoiceId = (typeof REEL_VOICE_IDS)[number];

export interface ReelVoice {
  id: ReelVoiceId;
  /** The Gemini TTS prebuilt voice name. */
  geminiName: string;
  /** The name that the UI shows. */
  label: string;
  gender: 'female' | 'male';
  tone: string;
}

export const REEL_VOICES: ReadonlyArray<ReelVoice> = [
  { id: 'kore', geminiName: 'Kore', label: 'Nova', gender: 'female', tone: 'firm, confident' },
  { id: 'zephyr', geminiName: 'Zephyr', label: 'Skye', gender: 'female', tone: 'bright' },
  { id: 'aoede', geminiName: 'Aoede', label: 'Rae', gender: 'female', tone: 'breezy' },
  { id: 'sulafat', geminiName: 'Sulafat', label: 'June', gender: 'female', tone: 'warm' },
  { id: 'puck', geminiName: 'Puck', label: 'Jax', gender: 'male', tone: 'upbeat' },
  { id: 'charon', geminiName: 'Charon', label: 'Cole', gender: 'male', tone: 'informative, deep' },
  { id: 'achird', geminiName: 'Achird', label: 'Theo', gender: 'male', tone: 'friendly' },
  { id: 'algenib', geminiName: 'Algenib', label: 'Rook', gender: 'male', tone: 'gravelly' },
];

export function isReelVoiceId(value: string): value is ReelVoiceId {
  return (REEL_VOICE_IDS as ReadonlyArray<string>).includes(value);
}

/** Returns the voice row of an id. Every `ReelVoiceId` has a row (the test checks it). */
export function reelVoiceById(id: ReelVoiceId): ReelVoice {
  const voice = REEL_VOICES.find((row) => row.id === id);
  if (voice === undefined) {
    throw new Error(`Unknown reel voice: ${id}`);
  }
  return voice;
}
